import { Tooltip } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { storeSelector } from "../../redux/slices/storeSlice";
import { getTotals } from "./utils/getTotals";
import usendLogo from "../../assets/img/usendlogo.png";
import adminAvatar from "../../portals/admin/assets/admin-user.svg";
import { ADMIN_DISPLAY_NAME } from "../AdminTopbar";
import styles from "./adminNavigation.module.css";

export const ADMIN_NAV_WIDTH = { expanded: 240, collapsed: 60 };

export const Svg = ({ children }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);

export const icons = {
  dashboard: (
    <Svg>
      <path d="M3 10.5 12 3l9 7.5" />
      <path d="M5 9v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V9" />
      <path d="M10 21v-6h4v6" />
    </Svg>
  ),
  orders: (
    <Svg>
      <path d="M22 12h-6l-2 3h-4l-2-3H2" />
      <path d="M5.5 5.1 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.5-6.9A2 2 0 0 0 16.8 4H7.2a2 2 0 0 0-1.7 1.1z" />
    </Svg>
  ),
  products: (
    <Svg>
      <path d="M12.6 2.6A2 2 0 0 0 11.2 2H4a2 2 0 0 0-2 2v7.2a2 2 0 0 0 .6 1.4l8.7 8.7a2.4 2.4 0 0 0 3.4 0l6.6-6.6a2.4 2.4 0 0 0 0-3.4z" />
      <circle cx="7.5" cy="7.5" r="1" />
    </Svg>
  ),
  shipping: (
    <Svg>
      <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2" />
      <path d="M15 18H9" />
      <path d="M19 18h2a1 1 0 0 0 1-1v-3.6a1 1 0 0 0-.2-.6l-3.5-4.4a1 1 0 0 0-.8-.4H14" />
      <circle cx="17" cy="18" r="2" />
      <circle cx="7" cy="18" r="2" />
    </Svg>
  ),
  users: (
    <Svg>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" />
    </Svg>
  ),
  payments: (
    <Svg>
      <path d="M3 21h18" />
      <path d="M5 21V10" />
      <path d="M19 21V10" />
      <path d="M9.5 21V10" />
      <path d="M14.5 21V10" />
      <path d="M12 3 3 8h18z" />
    </Svg>
  ),
  reports: (
    <Svg>
      <path d="M4 20V12" />
      <path d="M10 20V4" />
      <path d="M16 20v-6" />
      <path d="M22 20H2" />
    </Svg>
  ),
  referrals: (
    <Svg>
      <circle cx="9" cy="8" r="4" />
      <path d="M2 21v-1a6 6 0 0 1 6-6h2" />
      <path d="M18 14v6" />
      <path d="M21 17h-6" />
    </Svg>
  ),
  tracking: (
    <Svg>
      <path d="M12 21s-7-6.2-7-11a7 7 0 0 1 14 0c0 4.8-7 11-7 11Z" />
      <circle cx="12" cy="10" r="2.5" />
    </Svg>
  ),
  mail: (
    <Svg>
      <rect x="2" y="4" width="20" height="16" rx="2.5" />
      <path d="m22 7-9 5.7a2 2 0 0 1-2 0L2 7" />
    </Svg>
  ),
  collapse: (
    <Svg>
      <rect x="3" y="3" width="18" height="18" rx="3" />
      <path d="M9 3v18" />
    </Svg>
  ),
};

// `section` is the second path segment that marks the item as active.
// `countKey` is the route getTotals() knows for the badge.
const mainItems = [
  { section: "dashboard", text: "Dashboard", link: "/admin/dashboard", icon: icons.dashboard },
  {
    section: "orders",
    text: "Orders",
    link: "/admin/orders",
    icon: icons.orders,
    countKey: "/admin/orders",
    children: [
      { route: undefined, text: "All orders", link: "/admin/orders" },
      { route: "batch-process", text: "Batch processes", link: "/admin/orders/batch-process" },
      { route: "dispute-list", text: "Disputes", link: "/admin/orders/dispute-list", countKey: "/admin/orders/dispute-list" },
      {
        route: "returned-to-overseas-warehouse",
        text: "Returned to overseas",
        link: "/admin/orders/returned-to-overseas-warehouse",
      },
    ],
  },
  { section: "products", text: "Products", link: "/admin/products", icon: icons.products, countKey: "/admin/products" },
  { section: "shipping", text: "Shipping", link: "/admin/shipping", icon: icons.shipping },
  { section: "users", text: "Users", link: "/admin/users", icon: icons.users, countKey: "/admin/users" },
  { section: "payments", text: "Payments", link: "/admin/payments", icon: icons.payments, countKey: "/admin/payments" },
  { section: "reports", text: "Reports", link: "/admin/reports", icon: icons.reports },
];

const growthItems = [
  { section: "referrals", text: "Affiliates", link: "/admin/referrals", icon: icons.referrals },
  { section: "tracking-pages", text: "Tracking pages", link: "/admin/tracking-pages", icon: icons.tracking },
  { section: "mail-trackers", text: "Mail trackers", link: "/admin/mail-trackers", icon: icons.mail },
];

export const formatCount = (n) => (n > 999 ? "999+" : String(n));

// One sidebar entry with its sub-items; shared by the admin and customer navigation.
export function NavItem({ item, expanded, section, subRoute, count }) {
  const active = section === item.section;
  const n = count(item.countKey);
  const link = (
    <Link
      to={item.link}
      className={`${styles.item} ${active ? styles.active : ""}`}
      aria-current={active && !subRoute ? "page" : undefined}
      aria-label={expanded ? undefined : item.text}
    >
      {item.icon}
      {expanded && <span className={styles.label}>{item.text}</span>}
      {expanded && n > 0 && <span className={styles.badge}>{formatCount(n)}</span>}
      {!expanded && n > 0 && <span className={styles.dot} />}
    </Link>
  );

  return (
    <li>
      {expanded ? (
        link
      ) : (
        <Tooltip title={item.text} placement="right" arrow>
          {link}
        </Tooltip>
      )}
      {expanded && active && item.children && (
        <ul className={styles.subList}>
          {item.children.map((child) => {
            const childActive = subRoute === child.route;
            const childCount = count(child.countKey);
            return (
              <li key={child.link}>
                <Link
                  to={child.link}
                  className={`${styles.subItem} ${childActive ? styles.active : ""}`}
                  aria-current={childActive ? "page" : undefined}
                >
                  <span className={styles.label}>{child.text}</span>
                  {childCount > 0 && <span className={styles.badge}>{formatCount(childCount)}</span>}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
}

export default function AdminNavigation({ expanded, onToggle }) {
  const { totalsOfAdmin } = useSelector(storeSelector);
  const [, , section, subRoute] = useLocation().pathname.split("/");

  const count = (key) => {
    const n = key ? Number(getTotals(totalsOfAdmin, key)) : 0;
    return n > 0 ? n : 0;
  };

  const renderItem = (item) => (
    <NavItem
      key={item.link}
      item={item}
      expanded={expanded}
      section={section}
      subRoute={subRoute}
      count={count}
    />
  );

  return (
    <aside
      aria-label="Admin navigation"
      className={`${styles.sidebar} ${expanded ? "" : styles.collapsed}`}
      style={{ width: expanded ? ADMIN_NAV_WIDTH.expanded : ADMIN_NAV_WIDTH.collapsed }}
    >
      <div className={styles.header}>
        {expanded ? (
          <>
            <img src={usendLogo} alt="" className={styles.logo} />
            <span className={styles.brand}>U-Send</span>
            <button type="button" className={styles.iconButton} onClick={onToggle} aria-label="Collapse sidebar">
              {icons.collapse}
            </button>
          </>
        ) : (
          <Tooltip title="Expand sidebar" placement="right" arrow>
            <button type="button" className={styles.logoButton} onClick={onToggle} aria-label="Expand sidebar">
              <img src={usendLogo} alt="" className={styles.logo} />
            </button>
          </Tooltip>
        )}
      </div>

      <nav className={styles.nav}>
        <ul className={styles.list}>{mainItems.map(renderItem)}</ul>
        {expanded ? <div className={styles.section}>Growth</div> : <div className={styles.divider} />}
        <ul className={styles.list}>{growthItems.map(renderItem)}</ul>
      </nav>

      <div className={styles.account}>
        <img src={adminAvatar} alt="" className={styles.avatar} />
        {expanded && (
          <>
            <span className={styles.accountName}>{ADMIN_DISPLAY_NAME}</span>
            <span className={styles.accountRole}>Admin</span>
          </>
        )}
      </div>
    </aside>
  );
}
